import {sendEvent} from '@github-ui/hydro-analytics'
import {Button, Dialog, Spinner, Stack, Text} from '@primer/react'
import {useCallback, useState} from 'react'
import {useNavigate} from '@github-ui/use-navigate'

import {useDeleteTask} from '../hooks/use-delete-task'
import styles from './DeleteTaskDialog.module.css'

interface DeleteTaskDialogProps {
  taskId: string
  repoOwner?: string
  repoName?: string
  closeDialog: () => void
  /** When true, navigates back to the task list after deletion (used on task view page) */
  navigateOnDelete?: boolean
}

export function DeleteTaskDialog({
  taskId,
  repoOwner,
  repoName,
  closeDialog,
  navigateOnDelete = false,
}: DeleteTaskDialogProps) {
  const [error, setError] = useState<string | null>(null)
  const navigate = useNavigate()

  const {mutate: deleteTask, isPending} = useDeleteTask({
    owner: repoOwner,
    repo: repoName,
    taskId,
  })

  const handleDelete = useCallback(() => {
    setError(null)
    deleteTask(undefined, {
      onSuccess: () => {
        sendEvent('agent_sessions.task_actions', {
          target: 'DELETE_SESSION',
          task_id: taskId,
        })
        closeDialog()
        if (navigateOnDelete) {
          const redirectPath = repoOwner && repoName ? `/${repoOwner}/${repoName}/agents` : '/copilot/agents'
          navigate(redirectPath)
        }
      },
      onError: () => {
        setError('Failed to delete session. Please try again.')
      },
    })
  }, [deleteTask, taskId, closeDialog, navigate, repoOwner, repoName, navigateOnDelete])

  return (
    <Dialog title="Delete session" onClose={closeDialog} width="medium">
      <div className={styles.container}>
        <p>Are you sure you want to delete this session? This action cannot be undone.</p>

        <Stack
          direction="horizontal"
          gap="condensed"
          align="center"
          justify="space-between"
          className={styles.buttonStack}
        >
          <div role="status">
            {error && (
              <Text size="medium" className={styles.error}>
                {error}
              </Text>
            )}
            {isPending && <Spinner size="small" />}
          </div>
          <Stack direction="horizontal" gap="condensed" align="center" justify="end">
            <Button onClick={closeDialog} disabled={isPending}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleDelete} disabled={isPending}>
              Delete
            </Button>
          </Stack>
        </Stack>
      </div>
    </Dialog>
  )
}
